import React, { useState, useEffect } from "react";
import { calculateApr, parseNumber, formatCurrency, clamp } from "../utils/calculatorLogic";
import RangeSlider from "./common/RangeSlider";
import { sharePdf } from "../utils/pdf/sharePdf";
import { generatePdf } from "../utils/pdf/generatePdf";
import { mapAprCalculatorData } from "../utils/pdf/pdfMapper";
import { createPdfCacheManager } from "../utils/pdf/pdfCacheManager";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ReadMoreText from "./ReadMoreText";
ChartJS.register(ArcElement, Tooltip, Legend);

function AprCalculator({ showHeader, headerTitle, headerDesc }) {
  const [loanAmount, setLoanAmount] = useState(8000000);
  const [tenureYears, setTenureYears] = useState(10);
  const [interestRate, setInterestRate] = useState(10);
  const [originationFee, setOriginationFee] = useState(800000);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSharingPdf, setIsSharingPdf] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const APPLY_URL = "/property-loan/apply";
  const DEFAULT_HEADER_TITLE = "Annual Percentage Rate (APR) Calculator";
  const DEFAULT_HEADER_DESC = "Understand the total cost of your loan, including interest and applicable charges, with a single view.";
  const shouldShowHeader = showHeader?.toLowerCase() === "true";
  const resolvedHeaderTitle = headerTitle?.trim() || DEFAULT_HEADER_TITLE;
  const resolvedHeaderDesc = headerDesc?.trim() || DEFAULT_HEADER_DESC;

  const apr = calculateApr(loanAmount,tenureYears,0,interestRate,originationFee);

  const isInvalidFee = originationFee >= loanAmount;
  const netDisbursed = loanAmount - originationFee;

  const chartData = {
    labels: ["Net Disbursed", "Origination Fee"],
    datasets: [
      {
        data: [netDisbursed, originationFee],
         backgroundColor: ["#5563c1", "#e04644"],
        borderWidth: 0,
        cutout: "70%",
        hoverBorderWidth: 2,
        hoverBorderColor: ["#5168C1", "#E04848"],
        hoverOffset: 0,
        borderRadius: 0,
        spacing: 0
      },
    ],
  };

  const chartOptions = {
    rotation: -90,
    circumference: 180,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
  enabled: false, // ❗ disable default
  external: function (context) {
    let tooltipEl = document.getElementById("glass-tooltip");

    // create element
    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = "glass-tooltip";
      tooltipEl.innerHTML = `<div class="glass-tooltip-inner"></div>`;
      document.body.appendChild(tooltipEl);
    }

    const tooltip = context.tooltip;

    // hide
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    const data = tooltip.dataPoints?.[0];
    if (!data) return;

    // set content
    tooltipEl.querySelector(".glass-tooltip-inner").innerHTML = `
      <div class="tooltip-title">${data.label}</div>
      <div class="tooltip-value">₹ ${data.raw.toLocaleString()}</div>
    `;

    // position
    const rect = context.chart.canvas.getBoundingClientRect();

tooltipEl.style.opacity = 1;
tooltipEl.style.position = "absolute";
tooltipEl.style.left =
  rect.left + window.scrollX + tooltip.caretX + "px";
tooltipEl.style.top =
  rect.top + window.scrollY + tooltip.caretY + "px";
tooltipEl.style.transform = "translate(-50%, -100%)";
tooltipEl.style.pointerEvents = "none";
  }
},
    },
  };

  const pdfManager = React.useRef(createPdfCacheManager({pdfType: "apr",generatePdf,debounceMs: 400})).current;

  const updateLoanAmount = (val) => { pdfManager.markInteracted(); setLoanAmount(val); };
  const updateTenureYears = (val) => { pdfManager.markInteracted(); setTenureYears(val); };
  const updateInterestRate = (val) => { pdfManager.markInteracted(); setInterestRate(val); };
  const updateOriginationFee = (val) => { pdfManager.markInteracted(); setOriginationFee(val); };

  useEffect(() => {
    const mapped = mapAprCalculatorData({loanAmount,years: tenureYears,months: 0,interestRate,originationFee,apr});
    setPdfData(mapped);
    pdfManager.scheduleCache(mapped);
  }, [loanAmount,tenureYears,interestRate,originationFee,apr]);

  const handlePdfDownload = async () => {
    if (!pdfData) return;
    setIsDownloadingPdf(true);
    try {
      const blob = await pdfManager.getBlob(pdfData);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "APR_Calculator_Result.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsDownloadingPdf(false);
    }
  };
  const shadowPlugin = {
  id: "shadowPlugin",
  beforeDraw(chart) {
    const { ctx } = chart;

    ctx.save();

    ctx.shadowColor = "rgba(0,0,0,0.32)"; // #00000052
    ctx.shadowBlur = 7.5;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 5;
  },
  afterDraw(chart) {
    chart.ctx.restore();
  }
};
const hoverGlowPlugin = {
  id: "hoverGlowPlugin",
  afterDatasetDraw(chart, args) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);

    meta.data.forEach((arc, index) => {
      if (arc.active && index === 0) { // only blue part
        ctx.save();

        ctx.shadowColor = "#5168C1";
        ctx.shadowBlur = 15;

        arc.draw(ctx);

        ctx.restore();
      }
    });
  }
};
  const handlePdfShare = async () => {
    if (!pdfData) return;
    setIsSharingPdf(true);
    try {
      const blob = await pdfManager.getBlob(pdfData);
      await sharePdf(blob, "APR_Calculator_Result.pdf");
    } finally {
      setIsSharingPdf(false);
    }
  };

  return (
  <div className="calc mt-4">
  {shouldShowHeader && (
              <div className="paradescwrap mobile-only-header">
                <p className="title large-body bold">{resolvedHeaderTitle}</p>
                <div className="description">
                  <p className="description-text regular-body regular neutral800">
                    <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                  </p>
                </div>
              </div>
            )}
    <div class="wrapthechart">
      <div class="chartwrapperformobile">
           <div className="chart-wrapper-mobile">
              <Doughnut data={chartData} options={chartOptions} plugins={[shadowPlugin, hoverGlowPlugin]}/>
              <div className="chart-center">
                <p class="your-emi">Your APR</p>
                {isInvalidFee ? (
                  <h3 style={{ color: "red", fontSize: "14px" }}>
                    Fee must be less than Loan Amount
                  </h3>
                ) : (
                  <>
                    <p className="currency">{apr.toFixed(2)}%</p>
                    <span>Annual Rate</span>
                  </>
                )}
              </div>
           </div>
            <div class="donughtmobile">
              <div className="legend-mobile d-flex">
                <div className="legend-item"><span className="dot interest-dot"></span><span className="principal font-w-700 font-14 neutral800">Net Disbursed</span></div>
                <div className="legend-item"><span className="dot principal-dot"></span><span className="font-w-700 font-14 neutral800 interest">Origination Fee</span></div>
              </div>
            </div>
      </div>



    <div className="emi-container max-w-desktop">
      <div className="emi-layout">

        {/* LEFT SIDE */}
        <div className="emi-left">
            {shouldShowHeader && (
              <div className="paradescwrap">
                <p className="title large-body bold">{resolvedHeaderTitle}</p>
                <div className="description">
                  <p className="description-text regular-body regular neutral800">
                    <ReadMoreText text={resolvedHeaderDesc} limit={200} />
                  </p>
                </div>
              </div>
            )}
          <div className="input-group">
            <div className="input-header">
              <label>Loan Amount</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(loanAmount)} min="100000" max="20000000" step="100000" onChange={(e) => updateLoanAmount(clamp(parseNumber(e.target.value), 0, 20000000))}/>
              </div>
            </div>
            <RangeSlider
              min={100000} max={20000000} step={100000} value={loanAmount} onChange={(e) => updateLoanAmount(clamp(Number(e.target.value), 100000, 20000000))}
              formatValue={(val) => {
                if (val >= 10000000) { return `₹ ${(val / 10000000).toFixed(2)} Crore`;}
                else { return `₹ ${(val / 100000).toFixed(0)} Lakh`;}
              }}
              marks={[
                { value: 100000, label: "₹1Lakh" },
                { value: 4000000, label: "₹40Lakh" },
                { value: 8000000, label: "₹80Lakh" },
                { value: 12000000, label: "₹1.2Cr" },
                { value: 16000000, label: "₹1.6Cr" },
                { value: 20000000, label: "₹2Cr" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Term (Years)</label>
              <div className="input-box">
                <span>Y</span>
                <input type="number" value={tenureYears} min="1" max="25" onChange={(e) => updateTenureYears(clamp(Number(e.target.value), 1, 25))}/>
              </div>
            </div>
            <RangeSlider
              min={1} max={25} step={1} value={tenureYears} onChange={(e) => updateTenureYears(clamp(Number(e.target.value), 1, 25))}
              formatValue={(val) => `${val} Years`}
              marks={[
                { value: 1, label: "1Y" },
                { value: 5, label: "5Y" },
                { value: 10, label: "10Y" },
                { value: 15, label: "15Y" },
                { value: 20, label: "20Y" },
                { value: 25, label: "25Y" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Rate of Interest (p.a.)</label>
              <div className="input-box">
                <span>%</span>
                <input type="number" value={interestRate} min="6" max="22" step="0.1" onChange={(e) => updateInterestRate(clamp(Number(e.target.value), 1, 22))}/>
              </div>
            </div>
            <RangeSlider
              min={6} max={22} step={0.1} value={Math.max(interestRate, 6)} onChange={(e) => updateInterestRate(clamp(Number(e.target.value), 6, 22))}
              formatValue={(val) => `${val}%`}
              marks={[
                { value: 6, label: "6%" },
                { value: 10, label: "10%" },
                { value: 14, label: "14%" },
                { value: 18, label: "18%" },
                { value: 22, label: "22%" }
              ]}
            />
          </div>

          <div className="input-group">
            <div className="input-header">
              <label>Origination Charges</label>
              <div className="input-box">
                <span>₹</span>
                <input type="text" value={formatCurrency(originationFee)} min="0" max="5000000" step="10000" onChange={(e) => updateOriginationFee(clamp(parseNumber(e.target.value), 0, 5000000))}/>
              </div>
            </div>
            <RangeSlider
              min={0} max={5000000} step={10000} value={originationFee} onChange={(e) => updateOriginationFee(clamp(Number(e.target.value), 0, 5000000))}
              formatValue={(val) => val >= 100000 ? `₹ ${(val / 100000).toFixed(0)} L` : `₹ ${formatCurrency(val)}`}
              marks={[
                { value: 0, label: "0K" },
                { value: 1000000, label: "10L" },
                { value: 2000000, label: "20L" },
                { value: 3000000, label: "30L" },
                { value: 4000000, label: "40L" },
                { value: 5000000, label: "50L" }
              ]}
            />
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="emi-right">
          <div class="emibg">
          <div className="emi-curvature">
            <div className="chart-wrapper">
              <Doughnut data={chartData} options={chartOptions} />
              <div className="chart-center">
                <p class="your-emi">Your APR</p>
                {isInvalidFee ? (
                  <h3 style={{ color: "red", fontSize: "14px" }}>
                    Fee must be less than Loan Amount
                  </h3>
                ) : (
                  <>
                    <p className="currency">{apr.toFixed(2)}%</p>
                    <span>Annual Rate</span>
                  </>
                )}
              </div>
            </div>
            <div className="legend">
              <div className="legend-item"><span className="dot principal-dot"></span><span className="principal font-w-700 font-14 neutral800">Net Disbursed</span></div>
              <div className="legend-item"><span className="dot interest-dot"></span><span className="font-w-700 font-14 neutral800 interest">Origination Fee</span></div>
            </div>

            <div className="summary">
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600 prin">Loan Amount</span>
                <span className="rupee font-w-700 font-14 neutral1000">₹<span className="numericvalue rupee font-w-500 font-14 neutral1000">{formatCurrency(loanAmount)}</span></span>
              </div>
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600 interesttotal">Origination Charges</span>
                <span className="rupee font-w-700 font-14 neutral1000">₹<span className="numericvaluerupee font-w-500 font-14 neutral1000">{formatCurrency(originationFee)}</span></span>
              </div>
              <div className="summary-row">
                <span className="font-w-500 font-14 neutral600">Net Disbursed</span>
                <span className="rupee font-w-700 font-14 neutral1000">₹<span className="numericvalue rupee font-w-500 font-14 neutral1000">{formatCurrency(netDisbursed)}</span></span>
              </div>
            </div>

            <div className="card-actions">
              <div className="icon-group">
                <button className="icon-btn" onClick={handlePdfShare} disabled={isSharingPdf}>
                    <img src={`/documents/d/global/share-icon`} alt="Share"/>
                    <span className="caption-body neutral800">
                      {isSharingPdf ? "Please wait.." : "Share"}
                    </span>
                </button>
                <button className="icon-btn" onClick={handlePdfDownload} disabled={isDownloadingPdf}>
                    <img src={`/documents/d/global/download-icon`} alt="Download"/>
                    <span className="caption-body neutral800">
                      {isDownloadingPdf ? "Please wait.." : "Download"}
                    </span>
                </button>
              </div>
            </div>
          </div>
          </div>

          <div className="left-actions other-calculator">
            <button className="apply-btn btn primary-button" onClick={() => { globalThis.location.href = APPLY_URL; }}>Apply Now</button>
            <button className="callback-btn shimmer-button" onClick={() => globalThis.openApplyModal?.()}>
              <span className="shimmer-text">Get a call back</span>
              <svg className="shimmer-border" viewBox="0 0 232 48" preserveAspectRatio="none">
                <rect x="1" y="1" width="230" height="46" rx="24" ry="24" pathLength="1"></rect>
              </svg>
            </button>
          </div>

        </div>
      </div>
    </div>
    </div>
	</div>
  );
}

export default AprCalculator;